import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import DashboardShell from "@/components/DashboardShell";
import { LayoutDashboard, Users2, UserCog, ScrollText, PlayCircle, Mail, FileBadge, BarChart3, BookOpen, Layers } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const LINKS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, testId: "nav-admin-dashboard" },
  { to: "/admin/executives", label: "Marketing Executives", icon: UserCog, testId: "nav-admin-executives" },
  { to: "/admin/students", label: "Students", icon: Users2, testId: "nav-admin-students" },
  { to: "/admin/inbox", label: "Inbox", icon: Mail, testId: "nav-admin-inbox" },
  { to: "/admin/audit", label: "Audit Log", icon: ScrollText, testId: "nav-admin-audit" },
  { to: "/admin/sample-report", label: "Sample Report", icon: FileBadge, testId: "nav-admin-sample-report" },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3, testId: "nav-admin-analytics" },
  { to: "/admin/how-it-works", label: "How It Works", icon: BookOpen, testId: "nav-admin-how-it-works" },
  { to: "/admin/questions", label: "Question Bank", icon: Layers, testId: "nav-admin-questions" },
  { to: "/me/demo", label: "Demo Mode", icon: PlayCircle, testId: "nav-admin-demo" },
];

export default function AdminStudents() {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [search, setSearch] = useState("");
  const [reassignTarget, setReassignTarget] = useState(null);
  const [reassignReason, setReassignReason] = useState("");
  const [retestTarget, setRetestTarget] = useState(null);
  const [retestReason, setRetestReason] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = () => api.get("/admin/students", { params: { search } }).then((res) => setStudents(res.data));
  useEffect(() => { load(); }, [search]);

  const activate = async (studentId) => {
    try {
      await api.post(`/admin/students/${studentId}/activate`);
      toast.success("License activated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not activate");
    }
  };

  const submitReassign = async () => {
    await api.post(`/admin/students/${reassignTarget}/reassign`, { new_marketing_executive_id: null, reason: reassignReason });
    toast.success("Attribution reassigned");
    setReassignTarget(null); setReassignReason(""); load();
  };

  const submitRetest = async () => {
    await api.post(`/admin/students/${retestTarget}/retest-override`, { reason: retestReason });
    toast.success("Retest override granted");
    setRetestTarget(null); setRetestReason(""); load();
  };

  const confirmDelete = async () => {
    try {
      await api.post(`/admin/students/${deleteTarget}/delete`);
      toast.success("Student deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete student");
    }
    setDeleteTarget(null); load();
  };

  return (
    <DashboardShell links={LINKS} activePath="/admin/students">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-heading text-2xl font-bold text-slate-900" data-testid="admin-students-heading">All Students</h1>
        <Input placeholder="Search by name or email" value={search} data-testid="admin-students-search-input" onChange={(e) => setSearch(e.target.value)} className="max-w-xs" />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <Table data-testid="admin-students-table">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Marketing Exec</TableHead>
              <TableHead>Tag</TableHead>
              <TableHead>License</TableHead><TableHead>Report</TableHead><TableHead>Next Eligible</TableHead><TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {students.map((s) => (
              <TableRow key={s.student_id} data-testid={`admin-student-row-${s.student_id}`}>
                <TableCell>{s.name}</TableCell>
                <TableCell>{s.email}</TableCell>
                <TableCell>{s.marketing_executive_id || "Direct"}</TableCell>
                <TableCell>
                  {s.payment_status && (
                    <Badge variant={s.payment_status === "free" ? "secondary" : "default"} data-testid={`payment-status-badge-${s.student_id}`}>
                      {s.payment_status === "free" ? "Free" : "Paid"}
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  {s.license_blocked ? (
                    <span className="text-xs font-semibold text-rose-600" data-testid={`license-blocked-flag-${s.student_id}`}>Blocked: license pool exhausted</span>
                  ) : s.license_status}
                </TableCell>
                <TableCell>{s.report_status}</TableCell>
                <TableCell className="text-xs">{s.next_eligible_assessment_at ? new Date(s.next_eligible_assessment_at).toLocaleDateString() : "—"}</TableCell>
                <TableCell className="space-x-1 whitespace-nowrap">
                  {(s.license_status === "NOT_ISSUED" || s.license_status === "PENDING") && (
                    <Button size="sm" data-testid={`admin-activate-button-${s.student_id}`} onClick={() => activate(s.student_id)} className="bg-indigo-600 hover:bg-indigo-700 rounded-full">
                      {s.license_status === "PENDING" ? "Top Up & Activate" : "Activate"}
                    </Button>
                  )}
                  {s.report_status === "READY" && (
                    <Button size="sm" variant="outline" data-testid={`admin-view-report-button-${s.student_id}`} onClick={() => navigate(`/admin/students/${s.student_id}/report`)}>View Report</Button>
                  )}
                  {s.aptitude_report_status === "READY" && (
                    <Button size="sm" variant="outline" data-testid={`admin-view-aptitude-report-button-${s.student_id}`} onClick={() => navigate(`/admin/students/${s.student_id}/aptitude-report`)}>Aptitude Report</Button>
                  )}
                  <Button size="sm" variant="outline" data-testid={`admin-reassign-button-${s.student_id}`} onClick={() => setReassignTarget(s.student_id)}>Reassign</Button>
                  <Button size="sm" variant="outline" data-testid={`admin-retest-button-${s.student_id}`} onClick={() => setRetestTarget(s.student_id)}>Retest Override</Button>
                  <Button size="icon" variant="outline" className="text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`admin-delete-student-button-${s.student_id}`} onClick={() => setDeleteTarget(s.student_id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!reassignTarget} onOpenChange={(v) => !v && setReassignTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reassign Attribution to Direct</DialogTitle></DialogHeader>
          <Label>Reason (required for audit)</Label>
          <Input value={reassignReason} data-testid="reassign-reason-input" onChange={(e) => setReassignReason(e.target.value)} />
          <DialogFooter><Button data-testid="reassign-submit-button" disabled={!reassignReason} onClick={submitReassign}>Confirm Reassignment</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!retestTarget} onOpenChange={(v) => !v && setRetestTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Authorize Retest Override</DialogTitle></DialogHeader>
          <Label>Reason (required for audit)</Label>
          <Input value={retestReason} data-testid="retest-reason-input" onChange={(e) => setRetestReason(e.target.value)} />
          <DialogFooter><Button data-testid="retest-submit-button" disabled={!retestReason} onClick={submitRetest}>Confirm Override</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-student-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this student permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the student's profile, assessment sessions, licenses and reports. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-student-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-student-confirm-button" className="bg-red-600 hover:bg-red-700" onClick={confirmDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardShell>
  );
}
